import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classnames from "classnames";

import { InternalButton } from "@sharedComponents/buttons";

import { showErrorToast } from "@stadium/services/react-toastify";
import WeglotNoTranslationWrapper from "@stadium/services/weglot/no-translation-wrapper";

import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import SaveBoxAlert from "@components/store/save-box-alert";
import UnsavedChangesAlert from "@components/shared/unsaved-changes-alert";

import { useSendItemsContext } from "@contexts/send-items-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { getCartOrderMedleySets } from "@selectors/cart";

import { getCountry } from "@selectors/country";

import {
  getProductFromBoxList,
  getProductRelatedData,
  handleQuantityOfProductInBox,
  removeProduct,
  getDefaultSelectedProducts,
} from "@helpers/orders/byo-box";
import { getCanBuyProduct, getMasterVariant } from "@helpers/product-details";

import usePopulateItems from "@hooks/cart/use-populate-items";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import { useStoreContext } from "@components/store/provider";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useProductDetailContext } from "@contexts/product-detail-context";

import styles from "./byo-box-island.module.scss";

import NoImage from "@assets/images/no-image.svg";
import { SAVE_BOX_ALERT } from "@constants/modal-types";
import { UPDATE_ORDER_ERROR_TOAST_ID } from "@constants/toast-ids";
import { ON_DEMAND_PRODUCT, LOCKER_PRODUCT } from "@constants/products/detail";
import {
  ANALYTICS_TARGET_ADD_TO_BAG,
  ANALYTICS_TARGET_ADDED_TO_BAG,
} from "@constants/analytics/shop";
import { ANALYTICS_EVENT_ACTION_VIEWED } from "@stadium/services/analytics/constants/default";
import { buildAddToBagSelectedAttributes } from "@components/store/product-detail/shared/add-to-bag";
import { getProductType, getProductCategory } from "@helpers/products/types";

const CLOSE_BOX_AFTER_DELAY = 3000;
const DEFAULT_CLEARED_BOX_ITEMS = [];

const ByoBoxIsland = ({ product, fetchedProducts }) => {
  const { addByoItemInCart, updateByoComponentsInCart, loading } = usePopulateItems();
  const { addByoItemInLocalCart, updateByoComponentsInLocalCart } = usePopulateLocalItems();
  const { formatPriceInDisplayStoreCurrency, convertPriceInDisplayStoreCurrency } =
    useStoreCurrencyConverter();

  const { isSendToMyself } = useSendItemsContext();

  const { isGuestOrPreviewMode, previewMode } = usePreviewModeContext();
  const minimumRequiredPointsForBox = product.priceIn;

  const defaultCountry = useSelector(getCountry);

  const canBuyParentProduct = getCanBuyProduct({ product });

  const {
    editProductIds: [medleyItemSetId],
  } = useProductDetailContext();

  const buttonText = medleyItemSetId ? "Update Bag" : "Add to Bag";

  const medleySets = useSelector(getCartOrderMedleySets);

  const [showBoxDetails, setShowBoxDetails] = useState(true);
  const [lockBoxToggle, setLockBoxToggle] = useState(false);
  const [oldBoxProductList, setOldBoxProductList] = useState([]);
  const { handleBoxProductList, boxProductList } = useContext(ProductListingContext);
  const { storeCurrencyCode } = useStoreContext();
  const withAnalytics = useWithAnalytics();

  const itemType = product?.lockerProduct ? LOCKER_PRODUCT : ON_DEMAND_PRODUCT;
  const zoneId = defaultCountry?.zoneId || "";

  useEffect(() => {
    // do not run this if user updates bag -- else this will revert cleared items to selected ones
    if (medleyItemSetId && medleySets?.length && boxProductList !== DEFAULT_CLEARED_BOX_ITEMS) {
      const defaultSelectedProducts = getDefaultSelectedProducts({ medleySets, medleyItemSetId });
      setOldBoxProductList(defaultSelectedProducts);
      handleBoxProductList(defaultSelectedProducts);
    }
  }, [medleySets, medleyItemSetId]);

  // if user add new item then we open BYO-box
  const oldBoxProductListLength = useRef(boxProductList.length);

  useEffect(() => {
    if (!lockBoxToggle) {
      setTimeout(() => {
        setShowBoxDetails(false);
      }, CLOSE_BOX_AFTER_DELAY);
      if (boxProductList.length > oldBoxProductListLength.current) {
        setShowBoxDetails(true);
      }
    }
    oldBoxProductListLength.current = boxProductList.length;
  }, [boxProductList.length, lockBoxToggle]);

  const displayedProducts = useMemo(
    () => getProductRelatedData({ boxProductList, fetchedProducts }),
    [boxProductList, fetchedProducts],
  );

  const getBagAnalyticsProperties = () => {
    const variant = getMasterVariant(product);
    const selected_attributes = buildAddToBagSelectedAttributes(variant);

    if (displayedProducts.length) {
      selected_attributes.included_items = displayedProducts.map((item) => ({
        name: item.name,
        quantity: item.count,
        product_id: item.externalId?.toString() || item.id?.toString(),
      }));
    }

    return {
      product_id: product.id,
      quantity: 1,
      selected_attributes,
      product_type: getProductType(product),
      category: getProductCategory(product),
    };
  };

  const { toggleAddedToBagModal } = useProductDetailContext();
  const onAddToBagSuccess = () => {
    const eventProperties = getBagAnalyticsProperties();

    withAnalytics(undefined, {
      target: ANALYTICS_TARGET_ADDED_TO_BAG,
      action: ANALYTICS_EVENT_ACTION_VIEWED,
      track: true,
      eventProperties,
    })();

    toggleAddedToBagModal({
      product,
      options: {
        customVendorName: "BUILD-YOUR-OWN",
        hidePrice: true,
      },
      variant: getMasterVariant(product),
      isCheckoutBtnRequired: isSendToMyself,
    });
    handleBoxProductList(DEFAULT_CLEARED_BOX_ITEMS);
    setShowBoxDetails(false);
  };

  const handleQuantity = ({ quantity, externalId }) => {
    handleQuantityOfProductInBox({
      externalId,
      newQuantity: quantity,
      product,
      boxProductList,
      handleBoxProductList,
    });
  };

  const handleZeroQuantity = (externalId) => {
    removeProduct({ externalId, boxProductList, handleBoxProductList });
  };

  const removeProductFromBox = (externalId) => {
    removeProduct({ externalId, boxProductList, handleBoxProductList });
  };

  const getSum = (total, { count, prices }) => {
    return total + count * prices[0];
  };

  const totalPoints = boxProductList.reduce(getSum, 0);
  const totalPercentage = minimumRequiredPointsForBox
    ? (totalPoints / minimumRequiredPointsForBox) * 100
    : 100;

  const disableButton =
    loading || totalPoints < minimumRequiredPointsForBox || !canBuyParentProduct;

  const addPreviewModeProduct = (payload) => {
    const variant = getMasterVariant(product);
    const previewModePayload = {
      ...payload,
      variant: { ...variant, name: product.name, slug: product.slug },
      storePrice: payload.medleyItemAttributes.reduce(
        (total, { quantity, storePrice }) => total + quantity * storePrice,
        0,
      ),
      medleyItemAttributes: payload.medleyItemAttributes.map((item) => ({
        ...item,
        productId: +item.product_id,
        optionValues: item.optionValues.map((optionValue) => ({
          ...optionValue,
          optionType: { name: optionValue.name },
          id: optionValue.optionId,
        })),
      })),
      itemType,
    };
    return addByoItemInLocalCart(previewModePayload);
  };

  const updatePreviewModeProduct = () => {
    const previewModePayload = {
      medleyItemSetId,
      medleyItemAttributes: displayedProducts.map(
        ({ externalId, prices, count, image, name, slug, optionValues }) => ({
          productId: +externalId,
          price: prices[0],
          quantity: count,
          images: [image],
          name,
          slug,
          optionValues: optionValues.map((optionValue) => ({
            ...optionValue,
            optionType: { name: optionValue.name },
            id: optionValue.optionId,
          })),
        }),
      ),
      price: displayedProducts.reduce(getSum, 0),
    };
    return updateByoComponentsInLocalCart(previewModePayload);
  };

  const handleAddToBag = () => {
    const payload = {
      medleyId: getMasterVariant(product)?.externalId,
      medleyItemAttributes: displayedProducts.map(
        ({ externalId, prices, count, image, name, slug, optionValues }) => ({
          product_id: externalId.toString(),
          storePrice: prices[0],
          quantity: count,
          images: [image],
          name,
          slug,
          optionValues,
          zone_id: zoneId,
          item_type: itemType,
        }),
      ),
      itemType,
      zoneId,
    };

    const eventProperties = getBagAnalyticsProperties();

    withAnalytics(undefined, {
      target: ANALYTICS_TARGET_ADD_TO_BAG,
      track: true,
      eventProperties,
    })();

    (isGuestOrPreviewMode ? addPreviewModeProduct(payload) : addByoItemInCart(payload))
      .then(onAddToBagSuccess)
      .catch((err) => {
        const errors = err?.response?.data?.errors;
        if (errors?.length) {
          errors.forEach((err) =>
            showErrorToast({
              message: err.detail,
              config: { toastId: UPDATE_ORDER_ERROR_TOAST_ID },
            }),
          );
          handleClear();
        }
      });
  };

  const handleUpdateBag = () => {
    const eventProperties = getBagAnalyticsProperties();

    withAnalytics(undefined, {
      target: ANALYTICS_TARGET_ADD_TO_BAG,
      track: true,
      eventProperties,
    })();

    if (isGuestOrPreviewMode) return updatePreviewModeProduct().then(onAddToBagSuccess);
    const itemsTobeUpdated = [
      ...boxProductList,
      ...oldBoxProductList
        .filter(({ externalId }) => !boxProductList.find(({ externalId: id }) => id === externalId))
        .map((item) => ({ ...item, count: 0 })),
    ];
    const payload = {
      medleyId: getMasterVariant(product)?.externalId,
      medleyItemSetId,
      medleyItemAttributes: itemsTobeUpdated.map(({ externalId, prices, count }) => ({
        product_id: externalId.toString(),
        storePrice: prices[0],
        quantity: count,
        zone_id: zoneId,
        item_type: itemType,
      })),
      itemType,
      zoneId,
    };
    updateByoComponentsInCart(payload).then(onAddToBagSuccess);
  };
  const handleCancel = () => {
    handleBoxProductList(oldBoxProductList);
  };

  const handleClear = () => {
    handleBoxProductList([]);
  };

  const renderClearBtn = () =>
    boxProductList.length ? (
      <InternalButton styleType="primary-outline" onClick={handleClear}>
        Clear
      </InternalButton>
    ) : (
      <></>
    );
  const handleBoxDetailsToggle = () => {
    setShowBoxDetails(!showBoxDetails);
    setLockBoxToggle(!showBoxDetails);
  };

  const renderOptionValues = ({ optionId, presentation, name }, index, optionValues) => {
    return (
      <Fragment key={optionId}>
        <span>
          {name}: {presentation}
        </span>
        {index != optionValues?.length - 1 && <span>|</span>}
      </Fragment>
    );
  };

  return (
    <>
      <div
        className={classnames("", {
          [styles.byoFloatingWidget]: true,
          [styles.openBYOWidget]: showBoxDetails,
          [styles.withDemoBar]: previewMode,
          "open-BYO-widget": showBoxDetails,
        })}
        data-byo-expand={showBoxDetails}
      >
        <div className={styles.mobileOverlay} onClick={handleBoxDetailsToggle} />
        <button
          onClick={handleBoxDetailsToggle}
          className={classnames("", {
            [styles.boxTogglerButton]: true,
            [styles.iconDown]: showBoxDetails,
            [styles.iconUp]: !showBoxDetails,
          })}
        >
          <span className={styles.mobileToggler} />
          <span className={styles.arrowIcon} />
        </button>
        {!showBoxDetails && <div className={styles.pill}>BUILD-YOUR-OWN</div>}
        {showBoxDetails && (
          <section>
            <div className={styles.widgetTopRow}>
              <div className={styles.imageBlock}>
                <img src={product.variantImages[0] || NoImage} alt="category image" />
              </div>
              <div className={styles.rightBlock}>
                <div className={styles.pill}>BUILD-YOUR-OWN</div>
                <div className={styles.productName}>{product.name}</div>
                <div className={styles.itemCount}>Item Total: {boxProductList.length}</div>
              </div>
            </div>
            <div className={styles.productList}>
              {Boolean(displayedProducts.length) &&
                displayedProducts.map(({ image, name, prices, externalId, optionValues = [] }) => {
                  return (
                    <div className={styles.productRow} key={externalId}>
                      <div className={styles.imageBlock}>
                        <img src={image} alt="prod_ing" />
                      </div>
                      <div className={styles.rightBlock}>
                        <div className={styles.detailBlock}>
                          <div className={styles.productName}>{name}</div>
                          <WeglotNoTranslationWrapper
                            className={`t-cl-1 ${styles.tilePrice}`}
                            enclosingElement="div"
                          >
                            {formatPriceInDisplayStoreCurrency(prices)}
                            <div>
                              {optionValues.map((option, index, array) =>
                                renderOptionValues(option, index, array),
                              )}
                            </div>
                          </WeglotNoTranslationWrapper>
                        </div>
                        <div>
                          <QuantitySelector
                            quantity={getProductFromBoxList({ externalId, boxProductList }).count}
                            setQuantity={(quantity) => handleQuantity({ quantity, externalId })}
                            minOrderableQuantity={0}
                            onZeroHandler={() => handleZeroQuantity(externalId)}
                            className="defaultStyle"
                            disabled={!canBuyParentProduct}
                          />
                        </div>
                        <div className={styles.removeBlock}>
                          <button
                            onClick={() => removeProductFromBox(externalId)}
                            className={styles.btnClose}
                          >
                            <span className={styles.iconClose} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
        )}
        <section>
          {showBoxDetails ? (
            <div className={styles.boxPointsBarWithDetail}>
              Box Total{" "}
              <WeglotNoTranslationWrapper className={styles.boldText}>
                {formatPriceInDisplayStoreCurrency(totalPoints, storeCurrencyCode, {
                  formatZero: true,
                })}{" "}
                {totalPoints < minimumRequiredPointsForBox && (
                  <span className="t-cl-2">
                    / {formatPriceInDisplayStoreCurrency(minimumRequiredPointsForBox)}
                  </span>
                )}
              </WeglotNoTranslationWrapper>
            </div>
          ) : (
            <div className={styles.boxPointsBar}>
              <div className={styles.productName}>{product.name}</div>
              {totalPoints < minimumRequiredPointsForBox ? (
                <WeglotNoTranslationWrapper className={styles.pointsUsed} enclosingElement="div">
                  {convertPriceInDisplayStoreCurrency(totalPoints)}/
                  <span className="t-cl-2">
                    {formatPriceInDisplayStoreCurrency(minimumRequiredPointsForBox)}
                  </span>
                </WeglotNoTranslationWrapper>
              ) : (
                <div className={styles.pointsUsed}>
                  Minimum Reached <span className={`${styles.iconTick} t-bg-2`} />
                </div>
              )}
            </div>
          )}

          {totalPoints >= minimumRequiredPointsForBox && showBoxDetails ? (
            <>
              {medleyItemSetId && (
                <div className={styles.editBox}>
                  <span className={styles.boldText}>Add or remove items </span>
                  above before updating your bag.
                </div>
              )}
              {!medleyItemSetId && (
                <div className={styles.completeBox}>
                  <div className={styles.boldText}>Minimum Reached!</div>
                  <div>Add this box to bag or continue shopping.</div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.progressBar} id="minimum_box_points_progress_bar">
                <span
                  className={`t-bg-2 ${styles.progress}`}
                  style={{ width: `${totalPercentage}%` }}
                />
              </div>
              {showBoxDetails && totalPoints < minimumRequiredPointsForBox && (
                <div className={styles.pointsRemained}>
                  <WeglotNoTranslationWrapper>
                    {formatPriceInDisplayStoreCurrency(minimumRequiredPointsForBox - totalPoints)}
                  </WeglotNoTranslationWrapper>{" "}
                  more to {medleyItemSetId ? "update bag." : "checkout."}{" "}
                </div>
              )}
            </>
          )}
        </section>
        {showBoxDetails && (
          <footer className={styles.footerRow}>
            <div>
              {medleyItemSetId ? (
                <InternalButton styleType="primary-outline" onClick={handleCancel}>
                  Cancel
                </InternalButton>
              ) : (
                renderClearBtn()
              )}
            </div>

            <InternalButton
              onClick={medleyItemSetId ? handleUpdateBag : handleAddToBag}
              isLoading={loading}
              styleType="primary"
              disabled={disableButton}
            >
              {buttonText}
            </InternalButton>
          </footer>
        )}
      </div>
      <UnsavedChangesAlert
        type={SAVE_BOX_ALERT}
        Component={SaveBoxAlert}
        componentProps={{ product: product }}
        shouldConfirmLeave={boxProductList.length}
      />
    </>
  );
};

export default ByoBoxIsland;
